import {
  Abc,
  Apps,
  AutoAwesome,
  ContentCopy,
  Delete,
  KeyboardArrowRight,
  MoveDown,
  OpenInNew,
  PushPin,
  PushPinOutlined,
  Visibility,
} from '@mui/icons-material';
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  PopoverVirtualElement,
} from '@mui/material';
import { useEntityActions } from 'api/entity';
import BatchDeleteEntitiesModal from 'components/BatchDeleteEntitiesModal';
import { useModal } from 'components/modal';
import MoveEntitiesModal from 'components/MoveEntitiesModal';
import { useNotifications } from 'components/notify';
import { FinsightEntityFieldValue } from 'components/renderField';
import FinsightEntityPage from 'pages/finsightEntity';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FinsightEntity } from 'shared';
import { useSWRConfig } from 'swr';
import {
  addDockItem,
  addEntityToBoard,
  isEntityInDock,
  isEntityPinned,
  removeEntityFromBoard,
} from 'utils/dashboard';
import { sortFields } from 'utils/fields';

import { useWorkspaceId } from '../../api/providers/workspaceProvider';
import { useClipboard } from '../../providers/ClipboardProvider';
import { DataMaidEntityMenu } from './datamaidMenu';

interface EntityMenuProps {
  anchorEl: HTMLElement | PopoverVirtualElement | null;
  entity: FinsightEntity;
  onClose: () => void;
  isDirectEntityPage?: boolean; // true if rendered on entity page itself
}

// Wrapper component to render entity view in a modal with MemoryRouter
function EntityViewModal({ entityId }: { entityId: string }) {
  return <FinsightEntityPage entityId={entityId} />;
}

function EntityMenu(props: EntityMenuProps) {
  const { isInClipboard, copiedEntities } = useClipboard();

  const isMultiple = useMemo(() => {
    return isInClipboard(props.entity.id) || copiedEntities.length > 0;
  }, [copiedEntities.length, isInClipboard, props.entity.id]);

  if (isMultiple) {
    return <EntityMenuMultiple {...props} />;
  }

  return <EntityMenuSingle {...props} />;
}

function EntityMenuSingle({
  entity,
  onClose,
  anchorEl,
  isDirectEntityPage,
}: EntityMenuProps) {
  const navigate = useNavigate();
  const workspaceId = useWorkspaceId();
  const { cache, mutate } = useSWRConfig();
  const { deleteEntity } = useEntityActions();
  const { showNotification } = useNotifications();
  const { updateEntity } = useEntityActions();
  const { addToClipboard, isInClipboard, copiedEntities } = useClipboard();
  const { showModal } = useModal();
  const [copySubmenuAnchorEl, setCopySubmenuAnchorEl] =
    useState<HTMLElement | null>(null);
  const [dataMaidAnchorEl, setDataMaidAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  const [pinnedToBoard, setPinnedToBoard] = useState(() =>
    isEntityPinned(entity.id, workspaceId),
  );

  const [inDock, setInDock] = useState(() =>
    isEntityInDock(entity.id, workspaceId),
  );

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification(`${label} copied to clipboard`, {
        variant: 'success',
      });
    } catch (error) {
      showNotification(`Failed to copy ${label}`, {
        variant: 'error',
      });
    }
  };

  const handleCopyMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCopySubmenuAnchorEl(event.currentTarget);
  };

  const handleCopyMenuClose = () => {
    setCopySubmenuAnchorEl(null);
  };

  const handleDataMaidMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDataMaidAnchorEl(event.currentTarget);
  };

  const handleTogglePin = () => {
    if (pinnedToBoard) {
      removeEntityFromBoard(entity.id, workspaceId);
      setPinnedToBoard(false);
      showNotification('Entity unpinned from dashboard', {
        variant: 'success',
      });
    } else {
      addEntityToBoard(entity.id, workspaceId);
      setPinnedToBoard(true);
      showNotification('Entity pinned to dashboard', {
        variant: 'success',
      });
    }
    onClose();
  };

  const handleAddToDock = () => {
    if (!workspaceId) return;

    if (inDock) {
      showNotification('Entity is already in dock', {
        variant: 'warning',
      });
    } else {
      addDockItem({
        type: 'entity',
        entityId: entity.id,
        workspaceId: workspaceId,
        name: entity.name,
        icon:
          'image' in entity.extraFields && entity.extraFields.image
            ? `${entity.extraFields.image}`
            : undefined,
      });
      setInDock(true);
      showNotification('Entity added to dock', {
        variant: 'success',
      });
    }
    onClose();
  };

  return (
    <>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        sx={{
          '.MuiMenu-paper': {
            minWidth: 200,
          },
        }}
      >
        {copiedEntities.length > 0 ? (
          <MenuItem disabled>
            <ListItemText primary="Multi select" />
          </MenuItem>
        ) : (
          <MenuItem onClick={handleCopyMenuOpen}>
            <ListItemIcon>
              <Abc />
            </ListItemIcon>
            <ListItemText primary="Info" />
            <KeyboardArrowRight />
          </MenuItem>
        )}

        <MenuItem
          disabled={isInClipboard(entity.id)}
          onClick={() => {
            addToClipboard(entity);
            onClose();
          }}
        >
          <ListItemIcon>
            <ContentCopy />
          </ListItemIcon>
          <ListItemText primary="Add to clipboard" />
        </MenuItem>

        {/* Field Names Section */}
        {Object.values(entity.extraFieldsMeta || {}).filter(
          (x) => x.schema.type === 'enum',
        ).length > 0 && [
          <Divider key="quick-edit-title">Quick Edit</Divider>,
          sortFields(Object.values(entity.extraFieldsMeta))
            .filter((x) => x.schema.type === 'enum')
            .map((field) => (
              <FinsightEntityFieldValue
                key={field.name}
                field={field}
                value={entity.extraFields[field.name]}
                viewType="list"
                editable
                source="entity_menu"
                variant="menu"
                onChange={async (v) => {
                  showNotification('Updating entity...', {
                    loading: true,
                  });
                  await updateEntity(
                    entity.id,
                    {
                      extraFields: { [field.name]: v },
                    },
                    entity,
                  );
                  showNotification('Entity updated', {
                    variant: 'success',
                  });
                  onClose();
                }}
              />
            )),
        ]}
        <MenuItem onClick={handleDataMaidMenuOpen}>
          <ListItemIcon>
            <AutoAwesome />
          </ListItemIcon>
          <ListItemText primary="DataMaid" />
          <KeyboardArrowRight />
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleTogglePin}>
          <ListItemIcon>
            {pinnedToBoard ? <PushPin /> : <PushPinOutlined />}
          </ListItemIcon>
          <ListItemText
            primary={
              pinnedToBoard ? 'Unpin from dashboard' : 'Pin to dashboard'
            }
          />
        </MenuItem>
        <MenuItem onClick={handleAddToDock} disabled={inDock}>
          <ListItemIcon>
            <Apps />
          </ListItemIcon>
          <ListItemText primary={inDock ? 'Already in dock' : 'Add to dock'} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            showModal(
              entity.name,
              <EntityViewModal entityId={entity.id} />,
              '',
              {
                sx: {
                  maxWidth: '90vw',
                  width: '90vw',
                  maxHeight: '90vh',
                  height: '90vh',
                },
              },
            );
            onClose();
          }}
        >
          <ListItemIcon>
            <Visibility />
          </ListItemIcon>
          <ListItemText primary="View" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const entityUrl = `/workspace/${workspaceId}/entity/${entity.id}`;
            window.open(entityUrl, '_blank');
            onClose();
          }}
        >
          <ListItemIcon>
            <OpenInNew />
          </ListItemIcon>
          <ListItemText primary="Open in new tab" />
        </MenuItem>
        {entity.parent && [
          <MenuItem
            key="view-parent-entity"
            onClick={() =>
              navigate(`/workspace/${workspaceId}/entity/${entity.parent}`)
            }
          >
            <ListItemIcon>
              <OpenInNew />
            </ListItemIcon>
            <ListItemText primary="View Parent Entity" />
          </MenuItem>,
          <Divider key="view-parent-entity-divider" />,
        ]}
        <MenuItem
          onClick={async () => {
            try {
              const response = await deleteEntity(entity.id);
              if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message);
              }
              if (entity.parent) {
                Array.from(cache.keys())
                  .filter((x) =>
                    x.includes(
                      `/workspace/${workspaceId}/entity/${entity.parent}`,
                    ),
                  )
                  .forEach((key) => mutate(key));
              } else {
                Array.from(cache.keys())
                  .filter((x) =>
                    x.includes(
                      `/workspace/${workspaceId}/entities/${entity.types[0]}/root`,
                    ),
                  )
                  .forEach((key) => mutate(key));
              }
              if (isDirectEntityPage) {
                if (entity.parent) {
                  navigate(`/workspace/${workspaceId}/entity/${entity.parent}`);
                } else {
                  navigate(-1);
                }
              }
            } catch (e) {
              const message =
                e instanceof Error ? e.message : 'An error occurred';
              showNotification(message, {
                variant: 'error',
              });
              console.log(message);
            }
          }}
        >
          <ListItemIcon>
            <Delete color="error" />
          </ListItemIcon>
          <ListItemText primary="Delete" sx={{ color: 'error.main' }} />
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={copySubmenuAnchorEl}
        open={Boolean(copySubmenuAnchorEl)}
        onClose={handleCopyMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          onClick={() => {
            copyToClipboard(entity.name, 'Title');
            handleCopyMenuClose();
          }}
        >
          <ListItemIcon>
            <ContentCopy />
          </ListItemIcon>
          <ListItemText primary="Copy Title" />
        </MenuItem>

        {'description' in entity && entity.description && (
          <MenuItem
            onClick={() => {
              copyToClipboard(entity.description as string, 'Description');
              handleCopyMenuClose();
            }}
          >
            <ListItemIcon>
              <ContentCopy />
            </ListItemIcon>
            <ListItemText primary="Copy Description" />
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            const text =
              'description' in entity && entity.description
                ? `${entity.name}\n\n${entity.description}`
                : entity.name;
            copyToClipboard(text, 'All');
            handleCopyMenuClose();
          }}
        >
          <ListItemIcon>
            <ContentCopy />
          </ListItemIcon>
          <ListItemText primary="Copy All" />
        </MenuItem>
      </Menu>

      <DataMaidEntityMenu
        anchorEl={dataMaidAnchorEl}
        setAnchorEl={setDataMaidAnchorEl}
        entities={[entity]}
        onCloseParentMenu={onClose}
      />
    </>
  );
}

function EntityMenuMultiple({ entity, onClose, anchorEl }: EntityMenuProps) {
  const { copiedEntities } = useClipboard();

  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [dataMaidAnchorEl, setDataMaidAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  // Filter out the current entity from clipboard count
  const entitiesToMove = copiedEntities.filter(
    (clipboardEntity) => clipboardEntity.id !== entity.id,
  );

  const handleMoveModalOpen = () => {
    setMoveModalOpen(true);
    onClose();
  };

  const handleMoveModalClose = () => {
    setMoveModalOpen(false);
  };

  const handleDeleteModalOpen = () => {
    setDeleteModalOpen(true);
    onClose();
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
  };

  const handleDataMaidMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDataMaidAnchorEl(event.currentTarget);
  };

  return (
    <>
      <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={onClose}>
        {copiedEntities.length > 0 && (
          <MenuItem disabled>
            <ListItemText primary="Multi select" />
          </MenuItem>
        )}

        {entitiesToMove.length > 0 && (
          <MenuItem onClick={handleMoveModalOpen}>
            <ListItemIcon>
              <MoveDown />
            </ListItemIcon>
            <ListItemText primary={`Move here (${entitiesToMove.length})`} />
          </MenuItem>
        )}

        {copiedEntities.length > 0 && [
          <Divider key="datamaid-divider" />,
          <MenuItem key="datamaid-menu" onClick={handleDataMaidMenuOpen}>
            <ListItemIcon>
              <AutoAwesome />
            </ListItemIcon>
            <ListItemText primary="DataMaid" />
            <KeyboardArrowRight />
          </MenuItem>,
          <MenuItem key="delete-menu" onClick={handleDeleteModalOpen}>
            <ListItemIcon>
              <Delete color="error" />
            </ListItemIcon>
            <ListItemText
              primary={`Delete (${copiedEntities.length})`}
              sx={{ color: 'error.main' }}
            />
          </MenuItem>,
        ]}
      </Menu>

      <DataMaidEntityMenu
        anchorEl={dataMaidAnchorEl}
        setAnchorEl={setDataMaidAnchorEl}
        entities={copiedEntities}
        onCloseParentMenu={onClose}
      />

      <MoveEntitiesModal
        open={moveModalOpen}
        onClose={handleMoveModalClose}
        targetEntity={entity}
        onConfirm={() => {}}
      />

      <BatchDeleteEntitiesModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        entities={copiedEntities}
      />
    </>
  );
}

export default EntityMenu;
